import { useState } from 'react'
import { CommunicationLogFormData, CommunicationParticipant, CommunicationType, CommunicationDirection } from '../types/communicationLog'
import './CommunicationLogForm.css'

interface CommunicationLogFormProps {
  patientId: string
  taskId?: string
  initialData?: Partial<CommunicationLogFormData>
  onSubmit: (data: CommunicationLogFormData) => void
  onCancel: () => void
}

export function CommunicationLogForm({
  patientId,
  taskId,
  initialData,
  onSubmit,
  onCancel,
}: CommunicationLogFormProps) {
  const [formData, setFormData] = useState<CommunicationLogFormData>({
    patientId,
    taskId,
    communicationType: 'telephone',
    direction: 'incoming',
    participants: [],
    dateTime: new Date().toISOString().slice(0, 16),
    duration: undefined,
    summary: '',
    notes: '',
    ...initialData,
  })

  const [participantName, setParticipantName] = useState('')
  const [participantRole, setParticipantRole] = useState('')
  const [participantOrg, setParticipantOrg] = useState('')
  const [participantContact, setParticipantContact] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.summary.trim()) {
      alert('Summary is required')
      return
    }
    onSubmit(formData)
  }

  const handleAddParticipant = () => {
    if (participantName.trim()) {
      const participant: CommunicationParticipant = {
        name: participantName.trim(),
        role: participantRole || undefined,
        organization: participantOrg || undefined,
        contactInfo: participantContact || undefined,
      }
      setFormData({
        ...formData,
        participants: [...formData.participants, participant],
      })
      setParticipantName('')
      setParticipantRole('')
      setParticipantOrg('')
      setParticipantContact('')
    }
  }

  const handleRemoveParticipant = (index: number) => {
    setFormData({
      ...formData,
      participants: formData.participants.filter((_, i) => i !== index),
    })
  }

  return (
    <form className="communication-log-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="communication-type">Communication Type *</label>
        <select
          id="communication-type"
          value={formData.communicationType}
          onChange={(e) =>
            setFormData({ ...formData, communicationType: e.target.value as CommunicationType })
          }
          required
        >
          <option value="verbal">Verbal (In-person)</option>
          <option value="telephone">Telephone</option>
          <option value="email">Email</option>
          <option value="fax">Fax</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="direction">Direction *</label>
        <select
          id="direction"
          value={formData.direction}
          onChange={(e) =>
            setFormData({ ...formData, direction: e.target.value as CommunicationDirection })
          }
          required
        >
          <option value="incoming">Incoming</option>
          <option value="outgoing">Outgoing</option>
          <option value="internal">Internal</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="date-time">Date & Time *</label>
        <input
          id="date-time"
          type="datetime-local"
          value={formData.dateTime}
          onChange={(e) => setFormData({ ...formData, dateTime: e.target.value })}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="duration">Duration (minutes)</label>
        <input
          id="duration"
          type="number"
          min="0"
          value={formData.duration || ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              duration: e.target.value ? parseInt(e.target.value, 10) : undefined,
            })
          }
        />
      </div>

      <div className="form-group">
        <label>Participants</label>
        <div className="participant-input">
          <input
            type="text"
            placeholder="Name *"
            value={participantName}
            onChange={(e) => setParticipantName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Role"
            value={participantRole}
            onChange={(e) => setParticipantRole(e.target.value)}
          />
          <input
            type="text"
            placeholder="Organization"
            value={participantOrg}
            onChange={(e) => setParticipantOrg(e.target.value)}
          />
          <input
            type="text"
            placeholder="Contact Info"
            value={participantContact}
            onChange={(e) => setParticipantContact(e.target.value)}
          />
          <button type="button" onClick={handleAddParticipant} className="btn-add-participant">
            Add
          </button>
        </div>
        {formData.participants.length > 0 && (
          <div className="participant-list">
            {formData.participants.map((participant, index) => (
              <div key={index} className="participant-item">
                <span>
                  {participant.name}
                  {participant.role && ` (${participant.role})`}
                  {participant.organization && ` - ${participant.organization}`}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveParticipant(index)}
                  className="btn-remove-participant"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="summary">Summary *</label>
        <textarea
          id="summary"
          value={formData.summary}
          onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
          rows={5}
          required
          placeholder="Enter a summary of the communication..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="notes">Additional Notes</label>
        <textarea
          id="notes"
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value || undefined })}
          rows={3}
          placeholder="Any additional notes or details..."
        />
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-submit">
          {initialData ? 'Update Log' : 'Create Log'}
        </button>
        <button type="button" className="btn-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

